import { ContractedCarrierInDTO, ContractedCarrierOutDTO, ContractedPriceTableDTO, InputModeEnum } from '@api/ringil-api.v2.tsx';
import { generateUUID } from '@utils/utils.tsx';

/** A price level ("Úroveň N"). Its order is the position in the list; the name is derived from it (see renumberLevels). */
export interface PriceLevelForm {
  /** Stored level id on edit, a client id for a new level. Matrix cells and periods reference it. */
  key: string;
  name: string;
}

/**
 * A row of the price table: a manipulation unit and a laden factor; null = "Výchozí", the row used when no row of the
 * unit has the shipment's laden factor.
 */
export interface PriceRowForm {
  /** Client id; matrix cells reference it, so changing the unit or laden factor of a row keeps its prices. */
  key: string;
  cargoTemplateCode: string | null;
  cargoTemplateName?: string;
  ladenFactor: number | null;
}

/** A period of the plan ("Plán platnosti úrovní"), both days inclusive, ISO days. */
export interface PriceIntervalForm {
  /** Client-side identity only, for React keys and overlap errors. Never sent. */
  key: string;
  levelKey: string | null;
  validFrom: string | null;
  validTo: string | null;
}

export type PriceInputMode = 'BASIC' | 'PERCENTAGE';

/**
 * The price table structure shared by all contracted carriers of a Linka (or of one CONTRACTED shipment): levels,
 * rows (MU + laden factor), currency and the plan of levels.
 */
export interface PriceTableForm {
  inputMode: PriceInputMode;
  levels: PriceLevelForm[];
  /** The level named "Úroveň 0" - the others are numbered from it; in PERCENTAGE mode also the 0 % level. */
  defaultLevelKey: string | null;
  /** PERCENTAGE: difference between two neighbouring levels in %. */
  stepPercent: number | null;
  rows: PriceRowForm[];
  currency: string;
  intervals: PriceIntervalForm[];
}

/** levelKey → row key → price of one MU; null = empty cell. */
export type PriceMatrix = Record<string, Record<string, number | null>>;

/** A contracted carrier as edited in the forms (Linka and the CONTRACTED distribution). Empty users = the whole carrier. */
export interface ContractedCarrierForm {
  carrierId: string;
  carrierName: string;
  users: { id: string; fullName: string; email?: string }[];
  prices: PriceMatrix;
}

const toNumber = (value: number | string | null | undefined): number | null =>
  value === null || value === undefined || value === '' ? null : Number(value);

const sameLadenFactor = (a: number | null | undefined, b: number | null | undefined) =>
  (a === null || a === undefined) && (b === null || b === undefined) ? true : Number(a) === Number(b);

/** The table row with this unit and laden factor (null = Výchozí). */
export const findPriceRow = (table: PriceTableForm, cargoTemplateCode: string, ladenFactor: number | null | undefined): PriceRowForm | undefined =>
  table?.rows.find(r => r.cargoTemplateCode === cargoTemplateCode && sameLadenFactor(r.ladenFactor, ladenFactor));

/** Cells arrive as (level, MU, laden factor); they are keyed by the rows of [table]. Cells of unknown rows are dropped. */
export const fromContractedCarrierOutDTO = (dto: ContractedCarrierOutDTO, table: PriceTableForm): ContractedCarrierForm => {
  const prices: PriceMatrix = {};
  (dto.prices ?? []).forEach(cell => {
    const row = findPriceRow(table, cell.cargoTemplateCode, toNumber(cell.ladenFactor));
    if (!row) return;
    prices[cell.levelKey] = { ...(prices[cell.levelKey] ?? {}), [row.key]: toNumber(cell.price) };
  });
  return {
    carrierId: dto.carrierId,
    carrierName: dto.carrierName,
    users: (dto.users ?? []).map(u => ({ id: u.id, fullName: u.fullName, email: u.email })),
    prices,
  };
};

/** Only filled cells of existing levels and complete rows are sent. */
export const toContractedCarrierInDTO = (form: ContractedCarrierForm, table: PriceTableForm): ContractedCarrierInDTO => ({
  carrierId: form.carrierId,
  userIds: (form.users ?? []).map(u => u.id),
  prices: (table?.levels ?? []).flatMap(level =>
    (table?.rows ?? [])
      .filter(row => !!row.cargoTemplateCode)
      .map(row => ({ row, price: form.prices?.[level.key]?.[row.key] }))
      .filter(({ price }) => price !== null && price !== undefined && !Number.isNaN(price))
      .map(({ row, price }) => ({ levelKey: level.key, cargoTemplateCode: row.cargoTemplateCode, ladenFactor: row.ladenFactor ?? undefined, price })),
  ),
});

export const fromPriceTableDTO = (dto: ContractedPriceTableDTO): PriceTableForm => ({
  inputMode: dto.inputMode === InputModeEnum.PERCENTAGE ? 'PERCENTAGE' : 'BASIC',
  levels: (dto.levels ?? []).map(l => ({ key: l.key, name: l.name })),
  defaultLevelKey: dto.defaultLevelKey ?? dto.levels?.find(l => /\s0$/.test(l.name))?.key ?? dto.levels?.[0]?.key ?? null,
  stepPercent: toNumber(dto.stepPercent),
  rows: (dto.rows ?? []).map(r => ({ key: generateUUID(), cargoTemplateCode: r.cargoTemplateCode, cargoTemplateName: r.cargoTemplateName, ladenFactor: toNumber(r.ladenFactor) })),
  currency: dto.currency,
  intervals: (dto.intervals ?? []).map(i => ({ key: generateUUID(), levelKey: i.levelKey, validFrom: i.validFrom, validTo: i.validTo })),
});

export const toPriceTableDTO = (form: PriceTableForm): ContractedPriceTableDTO => ({
  inputMode: form.inputMode === 'PERCENTAGE' ? InputModeEnum.PERCENTAGE : InputModeEnum.BASIC,
  levels: form.levels.map(l => ({ key: l.key, name: l.name.trim() })),
  defaultLevelKey: form.defaultLevelKey ?? undefined,
  stepPercent: form.inputMode === 'PERCENTAGE' ? (form.stepPercent ?? undefined) : undefined,
  rows: form.rows.filter(r => !!r.cargoTemplateCode).map(r => ({ cargoTemplateCode: r.cargoTemplateCode, ladenFactor: r.ladenFactor ?? undefined })),
  currency: form.currency,
  intervals: form.intervals
    .filter(i => i.levelKey && i.validFrom && i.validTo)
    .map(i => ({ levelKey: i.levelKey, validFrom: i.validFrom, validTo: i.validTo })),
});
